/**
 * ComputePromptModal - Prompts users to spin up compute for interactive features.
 *
 * Appears when users try to:
 * - Edit a demo strategy
 * - Run a backtest
 * - Access interactive chart features
 * - Start the AI assistant
 *
 * Now includes a two-step flow:
 * 1. GPU selection
 * 2. Strategy template selection (for 'create' and 'backtest' actions)
 */

import { useState, useEffect } from 'react';
import { FiX, FiZap, FiCpu, FiClock, FiCheck, FiAlertCircle, FiArrowRight, FiArrowLeft } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { getApiUrl, extractErrorMessage } from '../../utils/api';
import StrategyTemplatePicker, { STRATEGY_TEMPLATES, encodeNotebookPath } from '../workspace/StrategyTemplatePicker';
import { useLaunchNotification } from '../../contexts/LaunchNotificationContext';

interface SemanticGPUOption {
  provider: 'aws' | 'gcp';
  provider_instance_id: string;
  gpu_model_name?: string;
  total_gpu_memory_gb?: number;
  on_demand_price_per_hour?: number;
  region: string;
  performance_category?: string;
  cost_tier?: string;
  display_name?: string;
}

interface RunningInstance {
  instance_id: string;
  provider: string;
  instance_type: string;
  region: string;
  gpu_type: string;
  hourly_cost: number;
  has_notebook: boolean;
  project_name: string;
  status: string;
}

interface ComputePromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  action?: 'edit' | 'backtest' | 'analyze' | 'create';
  onLaunch?: (instanceType: string) => void;
  /** Pre-selected template path — skips the template picker step */
  initialTemplatePath?: string;
}

type Step = 'gpu' | 'template';

export default function ComputePromptModal({
  isOpen,
  onClose,
  action = 'analyze',
  onLaunch,
  initialTemplatePath,
}: ComputePromptModalProps) {
  const navigate = useNavigate();
  const { addLaunch } = useLaunchNotification();
  const [gpuOptions, setGpuOptions] = useState<SemanticGPUOption[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Running instances state
  const [runningInstances, setRunningInstances] = useState<RunningInstance[]>([]);
  const [selectedRunningInstance, setSelectedRunningInstance] = useState<string | null>(null);
  const [useExisting, setUseExisting] = useState(false);

  // Two-step flow state
  const [step, setStep] = useState<Step>('gpu');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('momentum');
  const [selectedTemplatePath, setSelectedTemplatePath] = useState<string>(
    STRATEGY_TEMPLATES[0].path
  );

  // Show template picker for 'create' or 'backtest' unless a template was pre-selected
  const showTemplateStep = (action === 'create' || action === 'backtest') && !initialTemplatePath;

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setStep('gpu');
      setSelectedTemplate('momentum');
      setSelectedTemplatePath(STRATEGY_TEMPLATES[0].path);
      setUseExisting(false);
      setSelectedRunningInstance(null);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');

      // First, check for running instances
      try {
        const instancesResponse = await fetch(`${getApiUrl()}/api/v1/notebooks/available-instances`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (instancesResponse.ok) {
          const instancesData = await instancesResponse.json();
          const running = instancesData.filter((i: RunningInstance) =>
            i.status === 'running' || i.status === 'pending'
          );
          setRunningInstances(running);

          // Auto-select first running instance if any
          if (running.length > 0) {
            setSelectedRunningInstance(running[0].instance_id);
            setUseExisting(true);
          }
        }
      } catch (err) {
        console.debug('Could not fetch running instances:', err);
      }

      // Then fetch GPU options (for "launch new" option)
      try {
        const filters = encodeURIComponent(JSON.stringify({
          performance_categories: ['training_standard', 'inference_optimized'],
          cost_tiers: ['budget', 'medium'],
          providers: ['aws', 'gcp'],
          sort_by: 'price_performance',
        }));

        const response = await fetch(`${getApiUrl()}/api/gpu/discover?filters=${filters}&limit=4`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          setGpuOptions(data);
          setSelectedIndex(0);
        } else {
          const fallbackResponse = await fetch(`${getApiUrl()}/api/gpu/catalog?limit=4`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (fallbackResponse.ok) {
            const fallbackData = await fallbackResponse.json();
            setGpuOptions(fallbackData.instances || []);
          } else if (runningInstances.length === 0) {
            setError('Unable to load GPU options. Please try again.');
          }
        }
      } catch (err) {
        console.error('Error fetching GPU options:', err);
        if (runningInstances.length === 0) {
          setError('Unable to load GPU options. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const actionText = {
    edit: 'modify this strategy',
    backtest: 'run backtests',
    analyze: 'perform interactive analysis',
    create: 'create your own strategy',
  }[action];

  const handleTemplateSelect = (templateId: string, templatePath: string) => {
    setSelectedTemplate(templateId);
    setSelectedTemplatePath(templatePath);
  };

  const handleNext = () => {
    if (step === 'gpu' && showTemplateStep) {
      setStep('template');
    } else {
      handleLaunch();
    }
  };

  const handleBack = () => {
    if (step === 'template') {
      setStep('gpu');
    }
  };

  const handleUseExistingInstance = () => {
    if (!selectedRunningInstance) return;

    setLaunching(true);

    const rawNotebookPath = initialTemplatePath
      || (showTemplateStep && selectedTemplate !== 'blank' ? selectedTemplatePath : undefined);

    // Encode directory info if this is a multi-file template
    const notebookPath = rawNotebookPath ? encodeNotebookPath(rawNotebookPath) : undefined;

    // Store template info if selected
    if (rawNotebookPath) {
      sessionStorage.setItem('strategy_template', JSON.stringify({
        templateId: selectedTemplate,
        templatePath: rawNotebookPath,
        instanceId: selectedRunningInstance
      }));
    }

    // Store notebook launch params
    sessionStorage.setItem('notebook_launch_params', JSON.stringify({
      instance_id: selectedRunningInstance,
      notebook_path: notebookPath
    }));

    // Navigate to notebook session
    navigate('/dashboard/notebook/new-session');
  };

  const handleLaunch = async () => {
    // If using existing instance, handle that flow
    if (useExisting && selectedRunningInstance) {
      handleUseExistingInstance();
      return;
    }

    if (onLaunch && gpuOptions[selectedIndex]) {
      onLaunch(gpuOptions[selectedIndex].provider_instance_id);
      return;
    }

    const selectedGpu = gpuOptions[selectedIndex];
    if (!selectedGpu) return;

    setLaunching(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/api/compute/launch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: selectedGpu.provider,
          instance_type: selectedGpu.provider_instance_id,
          region: selectedGpu.region,
          pricing_type: 'ondemand',
        }),
      });

      if (response.ok) {
        const result = await response.json();
        // Use the actual cloud instance_id from the nested instance object
        // (not session_id which is a different identifier)
        const instanceId = result.instance?.instance_id || result.instance_id || result.session_id;

        // Determine notebook path based on action
        const rawNotebookPath = initialTemplatePath
          || (showTemplateStep && selectedTemplate !== 'blank' ? selectedTemplatePath : undefined);

        // Encode directory info if this is a multi-file template
        const notebookPath = rawNotebookPath ? encodeNotebookPath(rawNotebookPath) : undefined;

        // Persist notebook path so it survives navigation to NativeNotebookPage
        if (notebookPath && instanceId) {
          sessionStorage.setItem(`pending_notebook_for_${instanceId}`, notebookPath);
        }

        // Add launch notification - this will poll for status and show when ready
        addLaunch({
          instanceId,
          provider: selectedGpu.provider,
          instanceType: selectedGpu.provider_instance_id,
          gpuType: selectedGpu.gpu_model_name || selectedGpu.display_name,
          notebookPath,
        });

        // Close the modal - user will see the notification in the corner
        onClose();
      } else if (response.status === 402) {
        window.location.href = '/dashboard/billing?reason=insufficient_balance';
      } else {
        const errData = await response.json().catch(() => ({}));
        setError(extractErrorMessage(errData, 'Failed to launch instance. Please try again.'));
      }
    } catch (err) {
      console.error('Launch error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLaunching(false);
    }
  };

  const getBestForText = (gpu: SemanticGPUOption): string => {
    const vram = gpu.total_gpu_memory_gb ?? 0;
    if (vram <= 16) return 'Getting started, small backtests';
    if (vram <= 24) return 'Most strategies, ML training';
    return 'Large ML models, complex backtests';
  };

  const isRecommended = (index: number): boolean => {
    if (gpuOptions.length === 0) return false;
    const firstGoodOption = gpuOptions.findIndex(g => (g.total_gpu_memory_gb ?? 0) >= 24);
    return index === (firstGoodOption >= 0 ? firstGoodOption : 0);
  };

  const getButtonText = () => {
    if (launching) return useExisting ? 'Opening...' : 'Launching...';
    if (step === 'gpu' && showTemplateStep) return 'Choose Template';
    if (useExisting) return 'Use This Instance';
    return 'Launch & Open';
  };

  const canProceed = () => {
    if (loading) return false;
    if (useExisting) return !!selectedRunningInstance;
    return gpuOptions.length > 0;
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl max-w-lg w-full shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-profit/20 flex items-center justify-center">
              <FiZap className="w-5 h-5 text-profit" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                {step === 'gpu' ? 'Start Computing' : 'Choose a Starting Point'}
              </h2>
              <p className="text-sm text-gray-400">
                {step === 'gpu'
                  ? `Spin up a GPU to ${actionText}`
                  : 'Select a strategy template to get started'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicator for create flow */}
        {showTemplateStep && (
          <div className="px-5 pt-4">
            <div className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                step === 'gpu' ? 'bg-profit text-white' : 'bg-gray-700 text-gray-400'
              }`}>
                {step === 'template' ? <FiCheck className="w-3 h-3" /> : '1'}
              </div>
              <div className={`flex-1 h-0.5 ${step === 'template' ? 'bg-profit' : 'bg-gray-700'}`} />
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                step === 'template' ? 'bg-profit text-white' : 'bg-gray-700 text-gray-400'
              }`}>
                2
              </div>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-xs text-gray-500">GPU</span>
              <span className="text-xs text-gray-500">Template</span>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-5">
          {step === 'gpu' ? (
            <>
              {/* Running instances section */}
              {runningInstances.length > 0 && !loading && (
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-white">Use running instance</p>
                    <span className="text-xs text-green-400 bg-green-900/30 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
                      {runningInstances.length} active
                    </span>
                  </div>
                  <div className="space-y-2">
                    {runningInstances.slice(0, 2).map((instance) => (
                      <button
                        key={instance.instance_id}
                        onClick={() => {
                          setUseExisting(true);
                          setSelectedRunningInstance(instance.instance_id);
                        }}
                        className={`w-full p-3 rounded-lg border transition-all text-left ${
                          useExisting && selectedRunningInstance === instance.instance_id
                            ? 'border-green-500 bg-green-500/10'
                            : 'border-gray-800 bg-gray-800/50 hover:border-gray-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              useExisting && selectedRunningInstance === instance.instance_id
                                ? 'border-green-500 bg-green-500'
                                : 'border-gray-600'
                            }`}>
                              {useExisting && selectedRunningInstance === instance.instance_id && (
                                <FiCheck className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <div>
                              <span className="font-medium text-white">
                                {instance.project_name || instance.gpu_type}
                              </span>
                              <p className="text-xs text-gray-400 mt-0.5">
                                {instance.instance_type} • {instance.provider.toUpperCase()} • ${(instance.hourly_cost ?? 0).toFixed(2)}/hr
                              </p>
                            </div>
                          </div>
                          <span className="text-xs text-green-400">Running</span>
                        </div>
                      </button>
                    ))}
                  </div>

                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-800"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-gray-900 text-gray-500">or launch new</span>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-sm text-gray-400 mb-4">
                {runningInstances.length > 0 ? 'Launch a new instance:' : 'Choose an instance type based on your workload:'}
              </p>

              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="w-full p-4 rounded-lg border border-gray-800 bg-gray-800/50 animate-pulse">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 w-5 h-5 rounded-full border-2 border-gray-600" />
                          <div>
                            <div className="h-4 w-24 bg-gray-700 rounded mb-2" />
                            <div className="h-3 w-32 bg-gray-700 rounded mb-1" />
                            <div className="h-3 w-40 bg-gray-700 rounded" />
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="h-6 w-12 bg-gray-700 rounded mb-1" />
                          <div className="h-3 w-8 bg-gray-700 rounded" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : error && gpuOptions.length === 0 ? (
                <div className="text-center py-8">
                  <FiAlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                  <p className="text-gray-400 mb-4">{error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {gpuOptions.slice(0, 3).map((gpu, index) => (
                    <InstanceCard
                      key={`${gpu.provider}-${gpu.provider_instance_id}`}
                      instance={{
                        type: gpu.display_name || gpu.provider_instance_id,
                        provider: gpu.provider,
                        gpu: gpu.gpu_model_name || 'GPU',
                        vram: `${gpu.total_gpu_memory_gb ?? 0}GB`,
                        hourlyRate: gpu.on_demand_price_per_hour ?? 0,
                        recommended: isRecommended(index),
                        bestFor: getBestForText(gpu),
                      }}
                      selected={!useExisting && selectedIndex === index}
                      onSelect={() => {
                        setUseExisting(false);
                        setSelectedIndex(index);
                      }}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <StrategyTemplatePicker
              selectedTemplate={selectedTemplate}
              onSelect={handleTemplateSelect}
            />
          )}
        </div>

        {/* Error message if launch failed */}
        {error && gpuOptions.length > 0 && (
          <div className="px-5 pb-3">
            <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 rounded-lg p-3">
              <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-5 bg-gray-950/50 border-t border-gray-800 rounded-b-xl">
          {step === 'gpu' && (
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <FiClock className="w-4 h-4" />
                <span>Auto-shutdown after 15 min idle</span>
              </div>
              <div className="text-sm text-gray-400" title="Billing starts when the instance is created, including boot time (~1-3 min)">
                Billed from launch
              </div>
            </div>
          )}

          <div className="flex gap-3">
            {step === 'template' ? (
              <button
                onClick={handleBack}
                className="px-4 py-2.5 bg-gray-800 text-gray-300 rounded-lg font-medium hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <FiArrowLeft className="w-4 h-4" />
                Back
              </button>
            ) : (
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 bg-gray-800 text-gray-300 rounded-lg font-medium hover:bg-gray-700 transition-colors"
              >
                Continue Browsing
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={!canProceed() || launching}
              className="flex-1 px-4 py-2.5 bg-profit text-white rounded-lg font-medium hover:bg-profit/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {launching ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {getButtonText()}
                </>
              ) : (
                <>
                  {step === 'gpu' && showTemplateStep ? (
                    <>
                      Choose Template
                      <FiArrowRight className="w-4 h-4" />
                    </>
                  ) : useExisting ? (
                    <>
                      <FiArrowRight className="w-4 h-4" />
                      Use This Instance
                    </>
                  ) : (
                    <>
                      <FiZap className="w-4 h-4" />
                      Launch & Open
                    </>
                  )}
                </>
              )}
            </button>
          </div>

          {step === 'gpu' && (
            <p className="mt-3 text-center text-xs text-gray-500">
              Or{' '}
              <Link to="/dashboard/compute" className="text-profit hover:underline">
                view all instance options
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

interface InstanceCardProps {
  instance: {
    type: string;
    provider: 'aws' | 'gcp';
    gpu: string;
    vram: string;
    hourlyRate: number;
    recommended?: boolean;
    bestFor: string;
  };
  selected: boolean;
  onSelect: () => void;
}

function InstanceCard({ instance, selected, onSelect }: InstanceCardProps) {
  return (
    <button
      onClick={onSelect}
      className={`w-full p-4 rounded-lg border transition-all text-left ${
        selected
          ? 'border-profit bg-profit/10'
          : 'border-gray-800 bg-gray-800/50 hover:border-gray-700'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
            selected ? 'border-profit bg-profit' : 'border-gray-600'
          }`}>
            {selected && <FiCheck className="w-3 h-3 text-white" />}
          </div>

          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-white font-medium">{instance.type}</span>
              <span className={`px-1.5 py-0.5 text-xs rounded ${
                instance.provider === 'aws'
                  ? 'bg-orange-500/20 text-orange-400'
                  : 'bg-blue-500/20 text-blue-400'
              }`}>
                {instance.provider.toUpperCase()}
              </span>
              {instance.recommended && (
                <span className="px-1.5 py-0.5 bg-profit/20 text-profit text-xs rounded">
                  Recommended
                </span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-3 text-sm text-gray-400">
              <span className="flex items-center gap-1">
                <FiCpu className="w-3.5 h-3.5" />
                {instance.gpu}
              </span>
              <span>{instance.vram}</span>
            </div>
            <p className="mt-1 text-xs text-gray-500">{instance.bestFor}</p>
          </div>
        </div>

        <div className="text-right">
          <div className="text-lg font-semibold text-white">
            ${(instance.hourlyRate ?? 0).toFixed(2)}
          </div>
          <div className="text-xs text-gray-500">/hour</div>
        </div>
      </div>
    </button>
  );
}
